import { debounce, each, find } from "lodash";
import DropDown from "PFComponents/dropdown/dropdown";
import PropTypes from "prop-types";
import React from "react";

export default class TextArea extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      value: props.value,
      selectionStart: 0,
      selectionEnd: 0
    };

    this.inputRef = React.createRef();
    this.dropDown = null;
  }

  onAutoSize = debounce((scrollbarOffset) => {
    const { onAutoSize } = this.props;

    if (!this.isUnmounted && onAutoSize) {
      onAutoSize(scrollbarOffset);
    }
  }, 1);

  static getDerivedStateFromProps(nextProps) {
    const state = {};

    if (nextProps.controlledValue) {
      state.value = nextProps.value;
    }

    return state;
  }

  componentDidMount() {
    setTimeout(this.autoSize, 0);
  }

  componentDidUpdate(prevProps, prevState) {
    const { openEmojisDropdown, autoCompleteDropdowns } = this.props;
    const { selectionStart, selectionEnd, value } = this.state;

    this.autoSize();

    if (
      prevState.selectionStart === selectionStart &&
      prevState.selectionEnd === selectionEnd &&
      prevProps.openEmojisDropdown === openEmojisDropdown
    ) {
      return;
    }

    const head = value.substr(0, selectionStart);

    let autoCompleteDropdown, matchValue;

    if (!prevProps.openEmojisDropdown && openEmojisDropdown) {
      autoCompleteDropdown = find(autoCompleteDropdowns, (autoCompleteDropdown) =>
        autoCompleteDropdown.trigger.test(":")
      );

      matchValue = ":";
    }

    if (!autoCompleteDropdown && selectionStart === selectionEnd) {
      autoCompleteDropdown = find(autoCompleteDropdowns, (autoCompleteDropdown) =>
        autoCompleteDropdown.trigger.test(head)
      );

      if (autoCompleteDropdown) {
        matchValue = head.match(autoCompleteDropdown.trigger)[0];
      }
    }

    if (autoCompleteDropdown) {
      autoCompleteDropdown.data(matchValue, (options) => {
        this.setState({
          dropdownTrigger: options && options.length ? autoCompleteDropdown.trigger : null,
          dropdownOptions: options && options.length ? options : null
        });
      });
    } else {
      if (prevState.dropdownOptions) {
        this.setState({ dropdownTrigger: null, dropdownOptions: null });
      }
    }

    this.inputRef.current.focus();

    if (selectionStart || selectionStart === 0) {
      this.inputRef.current.selectionStart = selectionStart;
    }

    if (selectionEnd || selectionEnd === 0) {
      this.inputRef.current.selectionEnd = selectionEnd;
    }
  }

  componentWillUnmount() {
    this.isUnmounted = true;
  }

  autoSize = () => {
    const { viewportWidth } = this.state;
    const { simplifiedAutoSizeLogic } = this.props;

    if (this.inputRef.current) {
      const { current } = this.inputRef;
      const textLength = current.value.length;
      const newLinesMatches = current.value.match(/\n/g);
      const newLines = newLinesMatches ? newLinesMatches.length : 0;

      if (!simplifiedAutoSizeLogic && textLength < 50 && newLines === 0) {
        current.style.height = 0;
      } else if (!simplifiedAutoSizeLogic && textLength < 30 && newLines < 2) {
        current.style.height = 0;
      } else {
        current.style.height = `${this.inputRef.current.scrollHeight}px`;
      }

      const newViewportWidth = current.offsetWidth - current.scrollWidth;
      if (newViewportWidth !== viewportWidth) {
        this.onAutoSize(newViewportWidth);
        this.setState({ viewportWidth: newViewportWidth });
      }
    }
  };

  updateSelectionFromTarget = (target) => {
    const { selectionStart, selectionEnd } = target;
    this.setState({ selectionStart, selectionEnd });
  };

  applyChange = ({ value, selectionStart, selectionEnd }) => {
    const { autoCompleteReplace, onChange } = this.props;

    let head = value.substr(0, selectionStart);
    let body = value.substr(selectionStart, selectionEnd - selectionStart);
    let tail = value.substr(selectionEnd, value.length - selectionEnd);

    //Replace
    each(autoCompleteReplace, (replaceLogic) => {
      head = head.replace(replaceLogic.pattern, replaceLogic.replace);
      body = body.replace(replaceLogic.pattern, replaceLogic.replace);
      tail = tail.replace(replaceLogic.pattern, replaceLogic.replace);
    });

    const nextValue = head + body + tail;

    this.setState({
      value: nextValue,
      selectionStart: head.length,
      selectionEnd: head.length + body.length
    });

    onChange?.(nextValue);
  };

  handleClick = (event) => {
    const { onClick } = this.props;

    this.updateSelectionFromTarget(event.target);

    onClick?.(event);
  };

  handleKeyDown = (event) => {
    const { onKeyDown } = this.props;
    this.dropDown ? this.dropDown.handleKeyDown(event) : onKeyDown?.(event);
  };

  handleKeyUp = (event) => {
    const { onKeyUp } = this.props;

    this.updateSelectionFromTarget(event.target);

    onKeyUp?.(event);
  };

  handleChange = (event) => {
    const { value, selectionStart, selectionEnd } = event.target;
    this.applyChange({ value, selectionStart, selectionEnd });
  };

  handleDropdownClick = (item) => {
    const { value, selectionStart, selectionEnd, dropdownTrigger } = this.state;

    const head = value.substr(0, selectionStart).replace(dropdownTrigger, "") + item();
    const tail = value.substr(selectionEnd, value.length - selectionEnd);

    const nextValue = head + tail;

    const nextState = {
      value: nextValue,
      selectionStart: head.length,
      selectionEnd: head.length,
      dropdownOptions: null
    };

    this.setState(nextState);
    this.inputRef.current.focus();
    this.applyChange(nextState);
  };

  handleBlur = (event) => {
    const { onBlur } = this.props;

    // Hack needed to prevent closing dropdown on scrollbar click
    if (event.relatedTarget && event.relatedTarget.hasAttribute("data-dropdown-root")) {
      if (this.inputRef.current) {
        try {
          this.inputRef.current.focus();
        } catch (error) {}
      }
      return;
    }

    this.setState({ dropdownTrigger: null, dropdownOptions: null });

    onBlur?.(event);
  };

  render() {
    const {
      className,
      id,
      name,
      disabled,
      maxHeight,
      minHeight,
      maxLength,
      style,
      placeholder,
      autoFocus,
      displayAbove,
      onFocus,
      readOnly
    } = this.props;

    const { value, dropdownOptions } = this.state;

    return (
      <React.Fragment>
        {dropdownOptions && (
          <DropDown
            dropDownPointer={(reactElement) => (this.dropDown = reactElement)}
            options={dropdownOptions}
            selectedIndex={0}
            displayAbove={!!displayAbove}
            handleChange={this.handleDropdownClick}
            handleClose={() => this.setState({ dropdownOptions: null })}
          />
        )}
        <textarea
          className={className}
          ref={this.inputRef}
          id={id}
          name={name}
          value={value}
          disabled={disabled}
          maxLength={maxLength}
          placeholder={placeholder}
          // eslint-disable-next-line jsx-a11y/no-autofocus
          autoFocus={autoFocus}
          style={{ minHeight: minHeight, maxHeight: maxHeight, resize: "none", ...style }}
          onChange={this.handleChange}
          onKeyUp={this.handleKeyUp}
          onKeyDown={this.handleKeyDown}
          onFocus={onFocus}
          onBlur={this.handleBlur}
          onClick={this.handleClick}
          readOnly={readOnly}
        />
      </React.Fragment>
    );
  }
}

TextArea.propTypes = {
  id: PropTypes.string,
  className: PropTypes.string,
  name: PropTypes.string,
  value: PropTypes.string,
  controlledValue: PropTypes.bool,
  maxLength: PropTypes.number,
  disabled: PropTypes.bool,
  openEmojisDropdown: PropTypes.bool,
  placeholder: PropTypes.string,
  autoFocus: PropTypes.bool,
  displayAbove: PropTypes.bool,

  autoCompleteReplace: PropTypes.arrayOf(
    PropTypes.shape({
      pattern: PropTypes.instanceOf(RegExp),
      replace: PropTypes.func
    })
  ),

  autoCompleteDropdowns: PropTypes.arrayOf(
    PropTypes.shape({
      trigger: PropTypes.instanceOf(RegExp),
      data: PropTypes.func
    })
  ),

  minHeight: PropTypes.number,
  maxHeight: PropTypes.number,
  style: PropTypes.object,
  simplifiedAutoSizeLogic: PropTypes.bool,

  onChange: PropTypes.func,
  onClick: PropTypes.func,
  onKeyUp: PropTypes.func,
  onKeyDown: PropTypes.func,
  onFocus: PropTypes.func,
  onBlur: PropTypes.func,
  onAutoSize: PropTypes.func,
  readOnly: PropTypes.bool
};

TextArea.defaultProps = {
  id: "",
  name: "",
  value: "",
  controlledValue: false,
  disabled: false,
  openEmojisDropdown: false,
  placeholder: "",
  displayAbove: false,

  minHeight: 60,
  maxHeight: 200,
  simplifiedAutoSizeLogic: false,

  autoCompleteReplace: null,
  autoCompleteDropdowns: null,
  readOnly: false
};
